<template>
  <div>
    <inserter
      :enabled="!configurationActive"
      :class="[
        { ptm: resourceName === 'Entries' && tab.type === 'Details' },
        'pbm'
      ]"
      :indent="inserterIndent"
      @clicked="insertField(resourceName, tab)"
    />
    <ul class="list-unstyled">
      <draggable
        v-model="fieldList"
        draggable="li"
        animation="150"
        :drop-bubble="true"
        :dragover-bubble="true"
        :group="groupName"
        :disabled="!canDrag"
        @start="onDragStart"
        @end="onDragEnd"
        @add="onAdd"
      >
        <li
          v-for="field in fieldList"
          :key="'field-' + field.slug"
          :data-slug="field.slug"
        >
          <div
            :class="{
              'drag-handle': true,
              'drag-cursor': canDrag,
              dimmed: field.dimmed
            }"
          >
            <field :field="field" :resource="resource" :tab="tab">
              <span
                v-if="configuredField !== field.slug"
                slot="controls"
                :class="['edit-field', { hidden: dragging }]"
              >
                <a @click.prevent="onConfigure(field)">
                  <i class="af-icons af-icons-settings"></i>
                </a>
              </span>
            </field>
            <span
              v-if="canDrag"
              :class="['drag-field', { hidden: dragging }]"
              :style="{
                left: dragFieldLeftPosition + 'px',
                top: dragFieldTopPosition + 'px'
              }"
            >
              <i class="af-icons af-icons-up-down"></i>
            </span>
          </div>
          <inserter
            :enabled="!configurationActive"
            :class="[{ ptm: resourceName === 'Entries' }, 'pbm']"
            :indent="inserterIndent"
            @clicked="insertField(resourceName, tab, field)"
          />
        </li>
      </draggable>
    </ul>
    <confirmation-modal
      v-if="selectedFieldSlug"
      :key="`modal-target-configure-field-${selectedFieldSlug}`"
      :modal-id="`modal-target-configure-field-${selectedFieldSlug}`"
      :show-modal="confirmConfiguration"
      :confirmation="lang.get('miscellaneous.unsaved_changes')"
      :confirm-button-label="lang.get('buttons.continue')"
      :cancel-button-label="lang.get('buttons.cancel')"
      @closed="confirmConfiguration = false"
      @confirmed="configure"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import Draggable from 'vuedraggable';
import ConfirmationModal from '@/lib/components/Shared/ConfirmationModal';
import Inserter from '../../../lib/components/Shared/Inserter';
import Fields from '../Fields/Fields';

export default {
  inject: ['lang'],
  components: {
    ConfirmationModal,
    Draggable,
    Inserter
  },
  extends: Fields,
  props: {
    visibleFields: {
      type: Array,
      required: true
    },
    group: {
      type: String,
      default: null
    },
    lockTabs: {
      type: Boolean,
      required: false
    },
    tab: {
      type: Object,
      required: true
    },
    resource: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      dragging: false,
      selectedFieldSlug: null,
      confirmConfiguration: false
    };
  },
  computed: {
    ...mapState('entryForm', ['fieldTemplate']),
    ...mapState('entryFormConfiguration', [
      'configuredField',
      'configurationInProgress'
    ]),
    ...mapGetters('entryFormConfiguration', ['configurationActive']),
    groupName() {
      return this.group || this.resourceName;
    },
    visibleFieldIds() {
      return this.visibleFields.map(field => field.id);
    },
    draggableFields() {
      return this.fields.map(field => ({
        ...field,
        dimmed: !this.visibleFieldIds.includes(field.id)
      }));
    },
    fieldList: {
      get() {
        return this.draggableFields || [];
      },
      set(fields) {
        if (fields && fields.length) {
          this.saveFieldOrder({ fields });
        }
      }
    },
    dragFieldLeftPosition() {
      return this.resourceName === 'Contributors' ? -50 : -33;
    },
    dragFieldTopPosition() {
      return this.resourceName === 'Attachments' ? 3 : -10;
    },
    inserterIndent() {
      return this.resourceName === 'Contributors' ? -40 : -23;
    },
    canDrag() {
      return !this.configurationActive;
    }
  },
  created() {
    // Right off the bat make sure it's possible to insert new fields between
    // existing fields - the sorting algorithm separates fields by an order of 100:
    // 100, 200, 300...
    this.saveFieldOrder({ fields: this.fields });
  },
  methods: {
    ...mapActions('entryFormConfiguration', [
      'saveFieldOrder',
      'associateFieldWithTab',
      'configureField'
    ]),
    ...mapMutations('entryForm', ['addField']),
    ...mapMutations('entryFormConfiguration', [
      'setTabsLock',
      'setDraggedField'
    ]),
    onDragStart(event) {
      this.dragging = true;
      this.setTabsLock(this.lockTabs);
      this.setDraggedField({
        resource: this.resourceName,
        tabId: this.tab.id,
        fieldSlug: event.item.getAttribute('data-slug')
      });
    },
    onDragEnd() {
      this.dragging = false;
      this.setTabsLock(false);
    },
    onAdd(event) {
      const fieldSlug = event.item.getAttribute('data-slug');

      if (fieldSlug) {
        this.associateFieldWithTab({
          resource: this.resourceName,
          tabId: this.tab.id,
          tabSlug: this.tab.slug,
          fieldSlug: fieldSlug
        });
      }
    },
    onConfigure(field) {
      this.selectedFieldSlug = field.slug;

      if (this.configurationInProgress) {
        this.confirmConfiguration = true;
      } else {
        this.configure();
      }
    },
    configure() {
      this.configureField({ slug: this.selectedFieldSlug });
    },
    insertField(resource, tab, field) {
      let order;

      if (field) {
        order = field.order + 1;
      } else if (this.fieldList.length) {
        order = this.fieldList[0].order - 1;
      } else {
        order = 1;
      }

      const slug = 'newfield';

      this.addField({
        ...this.fieldTemplate,
        order: order,
        resource: resource,
        slug: slug,
        tabId: tab.id
      });

      this.configureField({ slug });
    }
  }
};
</script>

<style scoped>
.drag-field {
  position: absolute;
  padding: 10px;
}

.drag-field,
.edit-field {
  display: none;
}

ul li {
  position: relative;
}

.drag-handle:hover .drag-field,
.drag-handle:hover .edit-field {
  display: inline-block;
}

.drag-handle:hover .drag-field:hover {
  display: none;
}

.dimmed {
  opacity: 0.5;
}

.drag-field i,
.edit-field i {
  font-size: 18px;
}

.drag-cursor:hover {
  cursor: move;
}
</style>
